import { Eye, EyeOff, FileText, HatGlasses, ListRestart, LoaderCircle, Shredder } from 'lucide-preact'
import style from './app.module.css'
import { FileList, type FileInformation } from '../components/FileList'
import { useEffect, useRef, useState } from 'preact/compat';
import { compileDocument, getDocuments, getPostprocessors, resetAllFiles, type DocumentClassification } from '../api/Api';
import { FileUploader } from '../components/FileUploader';
import logo from '../assets/logo.svg';

export function App() {
  const [inputFiles, setInputFiles] = useState<FileInformation[]>([]);
  const [inputFilesLoading, setInputFilesLoading] = useState(true);

  const [postprocessorFiles, setPostprocessorFiles] = useState<FileInformation[]>([]);
  const [postprocessorFilesLoading, setPostprocessorFilesLoading] = useState(true);

  const [selectedDocument, setSelectedDocument] = useState<string>("");
  const [selectedDocumentClassification, setSelectedDocumentClassification] = useState<DocumentClassification>("Unclassified");
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string>("");

  const outputImage = useRef<HTMLImageElement>(null);

  useEffect(() => {
    void refreshAll();
  }, []);

  async function refreshDocuments() {
    setInputFilesLoading(true);
    try {
      const documents = (await getDocuments()).map(doc => ({
        type: 'document',
        name: doc.name,
        classification: doc.classification,
      })) as FileInformation[];
      setInputFiles(documents);
    } catch (error) {
      console.error("Failed to load documents:", error);
      alert(`Failed to load documents: ${error}`);
    }
    setInputFilesLoading(false);
  }

  async function refreshPostprocessors() {
    setPostprocessorFilesLoading(true);
    try {
      const postprocessorFiles = (await getPostprocessors()).map(name => ({ type: 'postprocessor', name, checkbox: 'forceUnchecked' })) as FileInformation[];
      setPostprocessorFiles(postprocessorFiles);
    } catch (error) {
      console.error("Failed to load postprocessors:", error);
      alert(`Failed to load postprocessors: ${error}`);
    }
    setPostprocessorFilesLoading(false);
    updatePluginCheckboxes(selectedDocument);
  }

  async function refreshAll() {
    await refreshDocuments();
    await refreshPostprocessors();
  }

  function handleDocumentClick(file: FileInformation) {
    setSelectedDocument(file.name);
    updatePluginCheckboxes(file.name);
  }

  async function handleRenderButtonClick(event: Event) {
    event.preventDefault();
    setRendering(true);
    try {
      const blob = await compileDocument(selectedDocument);
      setRenderError("");
      if (outputImage.current) {
        outputImage.current.src = URL.createObjectURL(blob);
      }
    } catch (error) {
      console.error("Failed to render:", error);
      if (outputImage.current) {
        outputImage.current.src = "";
      }
      setRenderError(`Failed to render: ${error}`);
    }
    setRendering(false);
  }

  async function handleResetButtonClick() {
    if (!confirm("Are you sure you want to delete all uploaded files? This cannot be undone.")) {
      return;
    }
    await resetAllFiles();
    await refreshAll();
    setSelectedDocument("");
    if (outputImage.current) {
      outputImage.current.src = "";
    }
    setRenderError("");
  }

  function updatePluginCheckboxes(fileName: string) {
    const classification = inputFiles.find(file => file.name === fileName)?.classification ?? "Classified";
    setSelectedDocumentClassification(classification);
    const redactPluginName = "redact.wasm";
    setPostprocessorFiles(prev => prev.map(file => {
      if (file.name === redactPluginName) {
        return { ...file, checkbox: classification === "Classified" ? "forceChecked" : "forceUnchecked" };
      }
      return file;
    }));
  }

  function handleFileNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    setSelectedDocument(input.value);
    updatePluginCheckboxes(input.value);
  }

  return (
    <>
      <header class={style.header}>
        <object class={style.logo} data={logo} aria-hidden="true" />
        <h1>Salzamt Auskunftssystem 2005</h1>
      </header>

      <div class={style.sideBySide}>
        <div>
          <section>
            <h2>Input Documents</h2>
            <FileList files={inputFiles} loading={inputFilesLoading} onFileClick={handleDocumentClick} />
            <FileUploader type="document" onUpload={() => { void refreshAll(); }} />
            <div class={style.legend}>
              <h3>Legend:</h3>
              <ul>
                <li><Eye /> Unclassified</li>
                <li><EyeOff /> Classified</li>
                <li><HatGlasses /> Top Secret</li>
              </ul>
            </div>
          </section>
          <section>
            <h2>Postprocessors</h2>
            <FileList files={postprocessorFiles} loading={postprocessorFilesLoading} />
            <FileUploader type="postprocessor" onUpload={() => { void refreshAll(); }} />
          </section>
          <section>
            <h2>Tools</h2>
            <div class={style.inputGroup}>
              <button onClick={() => { void refreshAll(); }}>
                <ListRestart />
                Reload files
              </button>
              <button onClick={() => { void handleResetButtonClick(); }}>
                <Shredder />
                Reset all uploaded files
              </button>
            </div>
          </section>
        </div>
        <div>
          <section>
            <h2>Document Options</h2>
            <form onSubmit={(e) => { void handleRenderButtonClick(e) }}>
              <div class={style.inputGroup}>
                <label for="input-file-name">Choose input:</label>
                <input class="input" id="input-file-name" type="text" list="documents-datalist" autoComplete="off"
                  value={selectedDocument} onInput={(e) => { handleFileNameInput(e); }}
                />
                <datalist id="documents-datalist">
                  {inputFiles.map(file => (
                    <option key={file.name} value={file.name}>{file.name}</option>
                  ))}
                </datalist>
              </div>
              <div class={style.inputGroup}>
                <button class={style.fullWidthButton} disabled={!selectedDocument || rendering} type="submit">
                  {rendering ? <span class="loader"><LoaderCircle /></span> : <FileText />}
                  Render
                </button>
              </div>
            </form>
          </section>
          <section>
            {
              selectedDocumentClassification === "Classified" &&
              <span>
                Information: The redaction postprocessor will always be applied to classified documents.
              </span>
            }
            {selectedDocumentClassification === "TopSecret" &&
              <span>
                Information: You are not allowed to use top secret documents.
              </span>
            }
            <h2>Output</h2>
            <div class={style.outputContainer}>
              {rendering &&
                <div class={style.outputLoader}>
                  <span class="loader">
                    <LoaderCircle size={64} />
                  </span>
                </div>
              }
              {renderError && <code class={style.outputError}>{renderError}</code>}
              <img ref={outputImage} class={style.outputImage} />
            </div>
          </section>
        </div >
      </div >
    </>
  )
}
